import { Icons } from "@app/shared";
import { AlertSeverity } from "@app/shared/src/models/Shared.AlertSeverity.gen";
import { formatInTimeZone } from "date-fns-tz";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlerts } from "../../state/AlertsContext";
import { Pages } from "../pages";
import { DeviceTab } from "../pages/facility/deviceTabs";

// An alert time rendered where the device actually is, always carrying its zone.
//
// The zone is never dropped: the bell is read by people in other zones than the device, and an
// unlabelled "2:53:47 AM" is the one format that can be silently wrong for them. The DATE is dropped
// when the alert is from today in that same zone, because "today" is most of what the bell holds and
// the date is then pure noise — but "today" is decided in the device's zone too, so an 11pm Denver
// alert doesn't read as today to someone in London for whom it is already tomorrow.
//
// Falls back to UTC rather than to the browser: a location with no timezone set is a data problem,
// and showing the viewer's own zone would hide it behind a plausible-looking local time.
function formatAlertTime(occurredAtUtc: string, timeZoneId?: string): string {
    const zone = timeZoneId && timeZoneId.trim().length > 0 ? timeZoneId : "UTC";
    const when = new Date(occurredAtUtc);
    if (Number.isNaN(when.getTime())) return "";
    try {
        const isToday = formatInTimeZone(when, zone, "yyyy-MM-dd") === formatInTimeZone(new Date(), zone, "yyyy-MM-dd");
        return formatInTimeZone(when, zone, isToday ? "h:mm:ss a zzz" : "MMM d, h:mm:ss a zzz");
    } catch {
        // A non-IANA id (a Windows zone name, say) makes formatInTimeZone throw. One bad location
        // must not blank the whole dropdown, so that row degrades to a labelled UTC time.
        return `${formatInTimeZone(when, "UTC", "MMM d, h:mm:ss a")} UTC`;
    }
}

// Top-nav bell with an unread badge and a dropdown of the latest active alerts.
// Critical count drives a red dot; warnings nudge the count visible too.
export const AlertBell: FC = () => {
    const { active } = useAlerts();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const criticalCount = useMemo(() => active.filter((a) => a.severity === AlertSeverity.Critical).length, [active]);
    const totalCount = active.length;

    // Straight to the device's Health tab rather than the alerts list: the bell has already told the
    // reader what and where, so the next question is always this device's state, not the row again.
    const openDeviceHealth = (deviceId: string) => {
        setOpen(false);
        navigate(Pages.DeviceDetails.getUrlWithQuery({ id: deviceId }, { tab: DeviceTab.Health }));
    };

    if (totalCount === 0 && !open) {
        // Render a quiet bell when there are no active alerts; still a button so
        // the user can click into the page.
        return (
            <button
                aria-label="View alerts"
                onClick={() => navigate(Pages.Alerts.getUrl())}
                className="mx-2 text-muted-body-text hover:text-body-text"
            >
                <Icons.AlertTriangle size={20} />
            </button>
        );
    }

    return (
        <div className="relative mx-2">
            <button
                aria-label={`${totalCount} active alerts`}
                onClick={() => setOpen((o) => !o)}
                className="relative text-muted-body-text hover:text-body-text"
            >
                <Icons.AlertTriangle size={20} className={criticalCount > 0 ? "text-red-500" : "text-yellow-500"} />
                <span
                    className={`absolute -top-2 -right-2 text-xs font-bold rounded-full px-1.5 py-0.5 ${criticalCount > 0 ? "bg-red-600" : "bg-yellow-500"} text-white`}
                >
                    {totalCount}
                </span>
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-80 bg-background-page rounded-md shadow-lg border border-separators-page z-50">
                    <div className="p-3 border-b border-separators-page font-bold">Active alerts</div>
                    <ul className="max-h-96 overflow-auto">
                        {active.slice(0, 10).map((a) => (
                            <li key={a.alertId} className="border-b border-separators-page last:border-b-0">
                                {/* The whole row is the target, not a link on the device name: the row is
                                    what reads as one thing, and the name is often the shortest text in it. */}
                                <button
                                    type="button"
                                    onClick={() => openDeviceHealth(a.deviceId)}
                                    className="w-full text-left px-3 py-2 hover:bg-background-top-nav focus:bg-background-top-nav focus:outline-none"
                                    title={`View health for ${a.deviceName ?? "this device"}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`text-xs font-bold ${a.severity === AlertSeverity.Critical ? "text-red-500" : "text-yellow-500"}`}>{AlertSeverity[a.severity]}</span>
                                        <span className="text-xs text-muted-body-text whitespace-nowrap">{formatAlertTime(a.occurredAtUtc, a.facilityTimezoneId)}</span>
                                    </div>
                                    {/* Name only. The guid prefix this used to fall back to identified nothing
                                        a person could act on — it was not the device key on the label either. */}
                                    <div className="text-sm font-medium flex items-center gap-1.5">
                                        <span>{a.deviceName ?? "Unknown device"}</span>
                                        {/* A recurring fault no longer opens one alert per bounce, so this
                                            badge is the only place the reader learns it has happened more
                                            than once — without it, collapsing the rows would have traded a
                                            noisy inbox for a quieter lie. */}
                                        {(a.reopenCount ?? 0) > 0 && (
                                            <span
                                                className="text-xs font-normal rounded px-1 bg-background-top-nav text-muted-body-text"
                                                title={`This condition has cleared and come back ${a.reopenCount} time${a.reopenCount === 1 ? "" : "s"} since the alert opened`}
                                            >
                                                recurring ×{(a.reopenCount ?? 0) + 1}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm text-muted-body-text">{a.message ?? "Alert active"}</div>
                                </button>
                            </li>
                        ))}
                        {active.length === 0 && (
                            <li className="px-3 py-2 text-muted-body-text text-sm">No active alerts</li>
                        )}
                    </ul>
                    <button
                        className="w-full text-center py-2 hover:bg-background-top-nav text-sm"
                        onClick={() => { setOpen(false); navigate(Pages.Alerts.getUrl()); }}
                    >
                        View all alerts
                    </button>
                </div>
            )}
        </div>
    );
};
